import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, Users, Clock, MessageCircle, ArrowRight } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

interface OnboardingTab {
  id: string;
  label: string;
  content: string;
}

const tabs: OnboardingTab[] = [
  {
    id: 'what',
    label: 'What peptides are',
    content:
      'Peptides are short chains of amino acids that act as signaling molecules in the body. Research peptides are used in laboratory and clinical studies to explore tissue repair, metabolism, cognitive function, and longevity pathways.',
  },
  {
    id: 'how',
    label: 'How they work',
    content:
      'They bind to specific receptors and trigger targeted cellular responses — instructing the body to repair tissue, regulate appetite, release hormones, or support immune signaling. The effect depends on the peptide sequence and its biological target.',
  },
  {
    id: 'benefits',
    label: 'Benefits',
    content:
      'Researchers study peptides for benefits ranging from faster recovery and lean body composition to enhanced energy, focus, and immune resilience. Each compound is selected for the pathway it modulates, from metabolic support to neuro-immune balance.',
  },
  {
    id: 'about',
    label: 'About AURA',
    content:
      'AURA is a UAE-based supplier of reference-grade lyophilized research peptides. Every lot is HPLC-verified, COA-documented, and dispatched with full batch traceability.',
  },
];

export default function OnboardingSection() {
  const [activeTab, setActiveTab] = useState<string>(tabs[0].id);
  const activeContent = tabs.find((t) => t.id === activeTab)?.content ?? tabs[0].content;

  return (
    <section className="border-t border-rp-line bg-rp-paper py-10 md:py-20">
      <div className="rp-container">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <SectionHeader
              eyebrow="Onboarding · for first-timers"
              title={
                <>
                  New to peptides? <em className="italic text-rp-forest">Start here</em>
                </>
              }
              description="Watch the explainer. It's 90 seconds and it'll click. Still confused? Message an advisor, there's always someone around."
              align="left"
            />

            <div className="mt-2 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`shrink-0 rounded-full border px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.06em] transition-colors duration-200 ${
                      isActive
                        ? 'border-rp-forest bg-rp-forest text-rp-paper'
                        : 'border-rp-line bg-rp-bone text-rp-moss hover:border-rp-forest hover:text-rp-forest'
                    }`}
                    aria-pressed={isActive}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="relative mt-6 min-h-[110px]">
              <AnimatePresence mode="wait">
                <motion.p
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="text-sm leading-relaxed text-rp-ink md:text-base"
                >
                  {activeContent}
                </motion.p>
              </AnimatePresence>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-rp-line bg-rp-bone px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-rp-forest transition-colors hover:border-rp-forest"
              >
                <MessageCircle size={14} />
                Message an advisor
                <ArrowRight size={12} />
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6 border-t border-rp-line pt-6">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-rp-line bg-rp-bone text-rp-forest">
                  <Users size={16} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-rp-forest">1,200+</p>
                  <p className="text-[11px] text-rp-moss">first-timers helped this month</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-rp-line bg-rp-bone text-rp-forest">
                  <Clock size={16} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-rp-forest">~3 min</p>
                  <p className="text-[11px] text-rp-moss">advisor reply time</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative"
          >
            <div className="group relative aspect-video overflow-hidden rounded-2xl border border-rp-line bg-gradient-to-br from-rp-forest to-rp-moss shadow-panel">
              <div className="absolute inset-0 opacity-20">
                <svg className="h-full w-full" viewBox="0 0 200 120" fill="none" aria-hidden="true">
                  <circle cx="160" cy="30" r="60" fill="url(#onbGrad)" />
                  <circle cx="40" cy="100" r="50" fill="url(#onbGrad2)" />
                  <defs>
                    <radialGradient id="onbGrad" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(160 30) rotate(90) scale(60)">
                      <stop stopColor="#0abab5" />
                      <stop offset="1" stopColor="#0abab5" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="onbGrad2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(40 100) rotate(90) scale(50)">
                      <stop stopColor="#4dd4cf" />
                      <stop offset="1" stopColor="#4dd4cf" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                </svg>
              </div>

              <button
                type="button"
                aria-label="Play 90-second explainer video"
                className="absolute inset-0 z-10 flex items-center justify-center"
              >
                <motion.span
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-rp-paper text-rp-forest shadow-panel"
                >
                  <PlayCircle size={32} strokeWidth={1.5} />
                </motion.span>
              </button>

              <div className="absolute bottom-4 left-4 z-10 rounded-full bg-rp-forest/80 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-paper backdrop-blur-sm">
                90-second explainer
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-rp-moss">
              <span className="h-1.5 w-1.5 rounded-full bg-rp-leaf" />
              Advisors are online now
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
